import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Ticket, Plus, Pencil, Trash2, Copy, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  code: "", discount_type: "percent", discount_value: 10,
  max_uses: "", valid_till: "", restricted_plan_ids: [],
  is_active: true, notes: "",
};

/** Super-Admin CRUD for SaaS coupon codes. */
export default function Coupons() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [openDlg, setOpenDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: c }, { data: p }] = await Promise.all([
        api.get("/saas/coupons/admin"),
        api.get("/payments/plans").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(c.items || []);
      setPlans(p.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Load failed");
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpenDlg(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code, discount_type: c.discount_type, discount_value: c.discount_value,
      max_uses: c.max_uses ?? "", valid_till: c.valid_till || "",
      restricted_plan_ids: c.restricted_plan_ids || [], is_active: c.is_active !== false,
      notes: c.notes || "",
    });
    setOpenDlg(true);
  };

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!form.code || form.code.length < 3) { toast.error("Code must be at least 3 characters"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        discount_value: Number(form.discount_value) || 0,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        valid_till: form.valid_till || null,
      };
      if (editing) {
        const { code: _, ...patch } = payload;
        await api.patch(`/saas/coupons/admin/${editing.id}`, patch);
        toast.success(`Coupon ${editing.code} updated`);
      } else {
        await api.post("/saas/coupons/admin", payload);
        toast.success(`Coupon ${payload.code} created`);
      }
      setOpenDlg(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?${c.total_uses > 0 ? "\n\nAlready used " + c.total_uses + " time(s) — it will be deactivated instead of deleted." : ""}`)) return;
    try {
      await api.delete(`/saas/coupons/admin/${c.id}`);
      toast.success(c.total_uses > 0 ? "Coupon deactivated" : "Coupon deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied ${code}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-ink-muted">SaaS console</div>
          <h1 className="font-heading text-2xl font-semibold" data-testid="coupons-title">Coupons</h1>
          <p className="text-sm text-ink-muted">Discounts for hospital subscriptions. Applied on the checkout page and reflected on the GST invoice.</p>
        </div>
        <Button onClick={openNew} className="bg-brand hover:bg-brand-700" data-testid="coupon-new-btn">
          <Plus size={14} className="mr-1.5" />New coupon
        </Button>
      </div>

      <Card data-testid="coupons-table-card">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-left">Discount</th>
              <th className="px-3 py-2 text-right">Usage</th>
              <th className="px-3 py-2 text-left">Valid till</th>
              <th className="px-3 py-2 text-left">Plans</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && (
              <tr><td colSpan="7" className="px-3 py-6 text-center text-sm text-ink-muted">No coupons yet. Create one to start giving discounts.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} data-testid={`coupon-row-${c.code}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <Ticket size={13} className="text-brand" />
                    <span className="font-mono font-semibold">{c.code}</span>
                    <button className="text-ink-muted hover:text-ink" onClick={() => copyCode(c.code)} title="Copy">
                      <Copy size={11} />
                    </button>
                  </div>
                  {c.notes && <div className="mt-0.5 text-[10px] text-ink-muted">{c.notes}</div>}
                </td>
                <td className="px-3 py-2 font-mono">
                  {c.discount_type === "percent" ? `${c.discount_value}%` : `₹${Number(c.discount_value).toLocaleString("en-IN")}`}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {c.total_uses || 0}
                  {c.max_uses ? <span className="text-ink-muted"> / {c.max_uses}</span> : <span className="text-ink-muted"> / ∞</span>}
                </td>
                <td className="px-3 py-2 text-xs">{c.valid_till ? c.valid_till.slice(0, 10) : "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {(c.restricted_plan_ids || []).length === 0
                    ? <span className="text-ink-muted">All plans</span>
                    : (c.restricted_plan_ids || []).map((pid) => plans.find(p => p.id === pid)?.name || pid).join(", ")}
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
                    {c.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`coupon-edit-${c.code}`}>
                      <Pencil size={12} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-rose-700" data-testid={`coupon-delete-${c.code}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={openDlg} onOpenChange={setOpenDlg}>
        <DialogContent className="max-w-lg" data-testid="coupon-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit coupon ${editing.code}` : "New coupon"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Code</Label>
              <Input
                value={form.code}
                onChange={(e) => set("code", e.target.value.toUpperCase())}
                disabled={!!editing}
                placeholder="DIWALI25"
                className="font-mono"
                data-testid="coupon-code-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount type</Label>
                <Select value={form.discount_type} onValueChange={(v) => set("discount_type", v)}>
                  <SelectTrigger data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat amount (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value</Label>
                <Input type="number" min="0" max={form.discount_type === "percent" ? 100 : undefined}
                  value={form.discount_value}
                  onChange={(e) => set("discount_value", e.target.value)}
                  data-testid="coupon-value-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Max uses (total)</Label>
                <Input type="number" min="1" value={form.max_uses}
                  onChange={(e) => set("max_uses", e.target.value)}
                  placeholder="unlimited"
                  data-testid="coupon-max-uses-input" />
              </div>
              <div>
                <Label>Valid till</Label>
                <Input type="date" value={(form.valid_till || "").slice(0, 10)}
                  onChange={(e) => set("valid_till", e.target.value ? `${e.target.value}T23:59:59+05:30` : "")}
                  data-testid="coupon-valid-till-input" />
              </div>
            </div>
            <div>
              <Label>Restrict to plans (optional)</Label>
              <div className="mt-1 flex flex-wrap gap-1.5 rounded-md border border-border p-2">
                {plans.length === 0 && <span className="text-xs text-ink-muted">No plans</span>}
                {plans.map((p) => {
                  const on = form.restricted_plan_ids.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => set("restricted_plan_ids", on
                        ? form.restricted_plan_ids.filter(x => x !== p.id)
                        : [...form.restricted_plan_ids, p.id])}
                      className={`rounded-full px-2 py-0.5 text-[11px] ${on ? "bg-brand text-white" : "bg-slate-100 text-ink hover:bg-slate-200"}`}
                    >
                      {p.name} · ₹{p.price_inr}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1 text-[10px] text-ink-muted">Leave empty to allow on all plans.</div>
            </div>
            <div>
              <Label>Notes (internal)</Label>
              <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. Diwali 2026 launch offer" data-testid="coupon-notes-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="coupon-active-switch" />
              <Label>Active</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpenDlg(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-700" data-testid="coupon-save-btn">
                <ClipboardCheck size={14} className="mr-1.5" />{saving ? "Saving…" : (editing ? "Save changes" : "Create coupon")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
